
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiUser, FiLock, FiMail, FiPhone, FiMapPin, FiGlobe, FiBriefcase, FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi';
import { Country, State } from 'country-state-city';
import '../Styles/Register.css';
import logo from '../Images/SAVI AI full.png';



const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    city: '',
    country: '',
    state: '',
    organization: '',
    jobTitle: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    city: '',
    country: '',
    state: '',
    organization: '',
    jobTitle: '',
    password: '',
    confirmPassword: '',
    general: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const navigate = useNavigate();
  
  // Create refs for form elements
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const mobileRef = useRef(null);
  const cityRef = useRef(null);
  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const organizationRef = useRef(null);
  const jobTitleRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  // Get countries and states from the package
  const countries = Country.getAllCountries();
  const [states, setStates] = useState([]);

  // Update states when country changes
  useEffect(() => {
    if (formData.country) {
      const countryStates = State.getStatesOfCountry(formData.country);
      setStates(countryStates);
    } else {
      setStates([]);
    }
  }, [formData.country]);

  // Phone number formats by country code (keeping some common formats)
  const phoneFormats = {
    'US': '(###) ###-####',
    'CA': '(###) ###-####',
    'GB': '#### ### ####',
    'AU': '#### ### ###',
    'IN': '##### #####',
    'DE': '### ########',
    'FR': '# ## ## ## ##',
    'JP': '##-####-####',
    'CN': '### #### ####',
    'BR': '(##) #####-####',
    'MX': '### ### ####',
    'IT': '### ### ####',
    'ES': '### ### ###',
    'NL': '## #### ####',
    'CH': '## ### ## ##',
    'SE': '##-### ## ##',
    'NO': '### ## ###',
    'DK': '## ## ## ##',
    'FI': '## ### ####'
  };

  // Input validation functions
  const validateFirstName = (name) => {
    if (!name.trim()) return 'First name is required';
    if (name.trim().length < 2) return 'First name must be at least 2 characters';
    if (!/^[a-zA-Z\s-']+$/.test(name)) return 'First name can only contain letters, spaces, hyphens, and apostrophes';
    return '';
  };

  const validateLastName = (name) => {
    if (!name.trim()) return 'Last name is required';
    if (name.trim().length < 2) return 'Last name must be at least 2 characters';
    if (!/^[a-zA-Z\s-']+$/.test(name)) return 'Last name can only contain letters, spaces, hyphens, and apostrophes';
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email is required';
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    
    // Check for corporate email domains (exclude personal email providers)
    const personalEmailDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
      'icloud.com', 'live.com', 'msn.com', 'me.com', 'mail.com',
      'ymail.com', 'rocketmail.com', 'protonmail.com', 'tutanota.com',
      'zoho.com', 'rediffmail.com', 'yahoo.co.in', 'yahoo.co.uk',
      'gmail.co.in', 'yahoo.in', 'rediff.com', 'fastmail.com',
      'guerrillamail.com', '10minutemail.com', 'tempmail.org',
      'mailinator.com', 'yopmail.com', 'temp-mail.org'
    ];
    
    const domain = email.toLowerCase().split('@')[1];
    if (personalEmailDomains.includes(domain)) {
      return 'Please use your corporate email address. Personal email accounts are not allowed for registration.';
    }
    
    return '';
  };

  // Phone number formatting function
  const formatPhoneNumber = (value, format) => {
    if (!value || !format) return value;
    
    const numbers = value.replace(/\D/g, '');
    let formatted = format;
    let numberIndex = 0;
    
    for (let i = 0; i < formatted.length && numberIndex < numbers.length; i++) {
      if (formatted[i] === '#') {
        formatted = formatted.substring(0, i) + numbers[numberIndex] + formatted.substring(i + 1);
        numberIndex++;
      }
    }
    
    formatted = formatted.replace(/#/g, '');
    return formatted;
  };

  const selectedCountryData = countries.find(c => c.isoCode === formData.country);

  const validateMobile = (mobile) => {
    if (!mobile.trim()) return 'Mobile number is required';
    
    const numbersOnly = mobile.replace(/\D/g, '');
    
    if (selectedCountryData) {
      const minLength = getMinPhoneLength(formData.country);
      const maxLength = getMaxPhoneLength(formData.country);
      
      if (numbersOnly.length < minLength || numbersOnly.length > maxLength) {
        return `Phone number should be ${minLength}-${maxLength} digits for ${selectedCountryData.name}`;
      }
    } else {
      if (numbersOnly.length < 7 || numbersOnly.length > 15) {
        return 'Please enter a valid phone number';
      }
    }
    
    return '';
  };

  const getMinPhoneLength = (countryCode) => {
    const lengths = {
      'US': 10, 'CA': 10, 'GB': 10, 'AU': 9, 'IN': 10, 'DE': 11, 'FR': 10,
      'JP': 10, 'CN': 11, 'BR': 11, 'MX': 10, 'IT': 10, 'ES': 9, 'NL': 9,
      'CH': 9, 'SE': 9, 'NO': 8, 'DK': 8, 'FI': 9, 'BE': 9, 'AT': 10,
      'IE': 9, 'PT': 9, 'PL': 9, 'CZ': 9, 'HU': 9, 'GR': 10, 'TR': 10,
      'RU': 10, 'KR': 10, 'SG': 8, 'MY': 9, 'TH': 9, 'ID': 10, 'PH': 10,
      'VN': 9, 'BD': 11, 'PK': 10, 'LK': 9, 'NP': 10, 'ZA': 9, 'NG': 10,
      'EG': 10, 'KE': 9, 'MA': 9, 'ET': 9, 'GH': 9, 'TZ': 9, 'UG': 9,
      'ZW': 9, 'ZM': 9
    };
    return lengths[countryCode] || 7;
  };

  const getMaxPhoneLength = (countryCode) => {
    const lengths = {
      'US': 10, 'CA': 10, 'GB': 11, 'AU': 10, 'IN': 10, 'DE': 12, 'FR': 10,
      'JP': 11, 'CN': 11, 'BR': 11, 'MX': 10, 'IT': 10, 'ES': 9, 'NL': 9,
      'CH': 9, 'SE': 9, 'NO': 8, 'DK': 8, 'FI': 10, 'BE': 10, 'AT': 13,
      'IE': 10, 'PT': 9, 'PL': 9, 'CZ': 9, 'HU': 9, 'GR': 10, 'TR': 10,
      'RU': 10, 'KR': 11, 'SG': 8, 'MY': 10, 'TH': 9, 'ID': 12, 'PH': 10,
      'VN': 10, 'BD': 11, 'PK': 11, 'LK': 10, 'NP': 10, 'ZA': 10, 'NG': 11,
      'EG': 11, 'KE': 10, 'MA': 10, 'ET': 10, 'GH': 10, 'TZ': 10, 'UG': 10,
      'ZW': 10, 'ZM': 10
    };
    return lengths[countryCode] || 15;
  };

  const validateCity = (city) => {
    if (!city.trim()) return 'City is required';
    return '';
  };

  const validateCountry = (country) => {
    if (!country.trim()) return 'Country/Region is required';
    const countryExists = countries.some(c => c.isoCode === country);
    if (!countryExists) return 'Please select a valid country';
    return '';
  };

  const validateState = (state) => {
    if (!state.trim()) return 'State/Province is required';
    if (formData.country && states.length > 0) {
      const stateExists = states.some(s => s.name === state || s.isoCode === state);
      if (!stateExists) return 'Please select a valid state/province';
    }
    return '';
  };

  const validateOrganization = (org) => {
    if (!org.trim()) return 'Organization is required';
    return '';
  };

  const validateJobTitle = (title) => {
    if (!title.trim()) return 'Job title is required';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
    if (!/(?=.*[!@#$%^&*])/.test(password)) return 'Password must contain at least one special character (!@#$%^&*)';
    return '';
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== password) return 'Passwords do not match';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    if (name === 'mobile' && selectedCountryData) {
      const format = phoneFormats[selectedCountryData.isoCode];
      if (format) {
        processedValue = formatPhoneNumber(value, format);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
      ...(name === 'country' && { state: '', mobile: '' })
    }));

    setErrors(prev => ({
      ...prev,
      [name]: '',
      general: ''
    }));
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        return validateFirstName(value);
      case 'lastName':
        return validateLastName(value);
      case 'email':
        return validateEmail(value);
      case 'mobile':
        return validateMobile(value);
      case 'city':
        return validateCity(value);
      case 'country':
        return validateCountry(value);
      case 'state':
        return validateState(value);
      case 'organization':
        return validateOrganization(value);
      case 'jobTitle':
        return validateJobTitle(value);
      case 'password':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value, formData.password);
      default:
        return '';
    }
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFocusedField(null);
    const errorMessage = validateField(name, value);
    
    setErrors(prev => ({
      ...prev,
      [name]: errorMessage
    }));
  };

  const validateForm = () => {
    const newErrors = {
      firstName: validateFirstName(formData.firstName),
      lastName: validateLastName(formData.lastName),
      email: validateEmail(formData.email),
      mobile: validateMobile(formData.mobile),
      city: validateCity(formData.city),
      country: validateCountry(formData.country),
      state: validateState(formData.state),
      organization: validateOrganization(formData.organization),
      jobTitle: validateJobTitle(formData.jobTitle),
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(formData.confirmPassword, formData.password),
      general: ''
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (validateForm()) {
        // Prepare the payload according to API requirements
        const payload = {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          mobile: formData.mobile.replace(/\D/g, ''), // Remove non-digit characters
          city: formData.city.trim(),
          country: selectedCountryData?.name || formData.country,
          state: formData.state.trim(),
          organization: formData.organization.trim(),
          job_title: formData.jobTitle.trim(),
          password: formData.password,
          confirm_password: formData.confirmPassword
        };

        // Make API call with error handling
        const response = await fetch(`${process.env.REACT_APP_SAVIAI_REGISTER_API}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (response.ok) {
          // Show success message instead of redirecting
          setRegistrationSuccess(true);
        } else {
          // Handle specific error cases
          if (data.email?.[0]?.includes('already exists')) {
            setErrors(prev => ({
              ...prev,
              email: 'This email is already registered',
              general: 'Registration failed. Please use a different email address.'
            }));
            emailRef.current?.focus();
            return;
          }

          // Handle other field-specific errors
          const fieldErrors = {};
          Object.keys(data).forEach(key => {
            const fieldMap = {
              first_name: 'firstName',
              last_name: 'lastName',
              job_title: 'jobTitle',
            };
            const formField = fieldMap[key] || key;
            fieldErrors[formField] = Array.isArray(data[key]) ? data[key][0] : data[key];
          });

          setErrors(prev => ({
            ...prev,
            ...fieldErrors,
            general: data.message || 'Registration failed. Please check the form and try again.'
          }));
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors(prev => ({
        ...prev,
        general: 'Network error. Please check your connection and try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Success message component
  const SuccessMessage = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 40px',
      textAlign: 'center',
      animation: 'fadeInUp 0.6s ease-out forwards'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        backgroundColor: '#4caf50',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '30px',
        animation: 'successPulse 0.6s ease-out',
        boxShadow: '0 0 20px rgba(76, 175, 80, 0.3)'
      }}>
        <FiCheckCircle size={40} color="white" />
      </div>
      
      <h2 style={{
        fontSize: '28px',
        fontWeight: '600',
        color: '#333',
        marginBottom: '16px',
        fontFamily: 'IBM Plex Sans, sans-serif'
      }}>
        Registration Successful!
      </h2>
      
      <p style={{
        fontSize: '16px',
        color: '#666',
        lineHeight: '1.6',
        marginBottom: '30px',
        maxWidth: '400px'
      }}>
        Welcome to Savi! Your account has been created successfully. 
        You can now sign in with your credentials.
      </p>
      
      <div style={{
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleSignIn}
          style={{
            backgroundColor: '#ff6900',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '14px 32px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontFamily: 'Inter, sans-serif'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#e25e00';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 15px rgba(255, 105, 0, 0.3)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#ff6900';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          Sign In Now
        </button>
        
        <button
          onClick={() => setRegistrationSuccess(false)}
          style={{
            backgroundColor: 'transparent',
            color: '#666',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '14px 32px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontFamily: 'Inter, sans-serif'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#f5f5f5';
            e.target.style.borderColor = '#bbb';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.borderColor = '#ddd';
          }}
        >
          Register Another Account
        </button>
      </div>
    </div>
  );

  if (registrationSuccess) {
    return (
      <div className="register-user-main-container">
        <header className="register-user-header">
          <div className="register-user-logo">
            <img src={logo} alt="SAVI AI" className="register-user-logo-image" />
          </div>
          <button className="register-user-signin-button" onClick={handleSignIn}>
            Sign In
          </button>
        </header>
        
        <div className="register-form-container">
          <div className="register-user-form-container">
            <SuccessMessage />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-user-main-container">
      <header className="register-user-header">
        <div className="register-user-logo">
          <img src={logo} alt="SAVI AI" className="register-user-logo-image" /> 
        </div>
        <button className="register-user-signin-button" onClick={handleSignIn}>
          Sign In
        </button>
      </header>
      
      <div className="register-form-container">
        <div className="register-user-form-container">
          <div className="register-user-form-header">
            <h1 className="register-user-form-title">
              Create Your Account
            </h1>
            <div className="register-user-title-underline"></div>
            <p className='register-user-form-title-p'>
              One Savi Account is all you need to access all Savi services
            </p>
          </div>

          {errors.general && (
            <div className="register-user-error-message slide-down">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="register-user-form">
            <div className="register-user-form-row">
              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper ${focusedField === 'firstName' ? 'focused' : ''} ${formData.firstName ? 'filled' : ''} ${errors.firstName ? 'error' : ''}`}>
                  <FiUser className="register-user-input-icon" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    onFocus={() => handleFocus('firstName')}
                    onBlur={handleBlur}
                    placeholder="First Name *"
                    className="register-user-input-field"
                    ref={firstNameRef}
                    required
                  />
                  <div className="register-user-input-border"></div>
                </div>
                {errors.firstName && (
                  <p className="register-user-field-error fade-in">{errors.firstName}</p>
                )}
              </div>

              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper ${focusedField === 'lastName' ? 'focused' : ''} ${formData.lastName ? 'filled' : ''} ${errors.lastName ? 'error' : ''}`}>
                  <FiUser className="register-user-input-icon" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    onFocus={() => handleFocus('lastName')}
                    onBlur={handleBlur}
                    placeholder="Last Name *"
                    className="register-user-input-field"
                    ref={lastNameRef}
                    required
                  />
                  <div className="register-user-input-border"></div>
                </div>
                {errors.lastName && (
                  <p className="register-user-field-error fade-in">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="register-user-form-row">
              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper ${focusedField === 'email' ? 'focused' : ''} ${formData.email ? 'filled' : ''} ${errors.email ? 'error' : ''}`}>
                  <FiMail className="register-user-input-icon" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => handleFocus('email')}
                    onBlur={handleBlur}
                    placeholder="Corporate Email Address *"
                    className="register-user-input-field"
                    ref={emailRef}
                    required
                  />
                  <div className="register-user-input-border"></div>
                </div>
                {errors.email && (
                  <p className="register-user-field-error fade-in">{errors.email}</p>
                )}
              </div>

              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper ${focusedField === 'city' ? 'focused' : ''} ${formData.city ? 'filled' : ''} ${errors.city ? 'error' : ''}`}>
                  <FiMapPin className="register-user-input-icon" />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    onFocus={() => handleFocus('city')}
                    onBlur={handleBlur}
                    placeholder="City *"
                    className="register-user-input-field"
                    ref={cityRef}
                    required
                  />
                  <div className="register-user-input-border"></div>
                </div>
                {errors.city && (
                  <p className="register-user-field-error fade-in">{errors.city}</p>
                )}
              </div>
            </div>

            <div className="register-user-form-row">
              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper ${focusedField === 'country' ? 'focused' : ''} ${formData.country ? 'filled' : ''} ${errors.country ? 'error' : ''}`}>
                  <FiGlobe className="register-user-input-icon" />
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    onFocus={() => handleFocus('country')}
                    onBlur={handleBlur}
                    className="register-user-input-field"
                    data-has-value={formData.country ? "true" : "false"}
                    ref={countryRef}
                    required
                  >
                    <option value="">Select country/region *</option>
                    {countries.map(country => (
                      <option key={country.isoCode} value={country.isoCode}>
                        {country.name} ({country.isoCode})
                      </option>
                    ))}
                  </select>
                  <div className="register-user-input-border"></div>
                </div>
                {errors.country && (
                  <p className="register-user-field-error fade-in">{errors.country}</p>
                )}
              </div>

              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper ${focusedField === 'state' ? 'focused' : ''} ${formData.state ? 'filled' : ''} ${errors.state ? 'error' : ''}`}>
                  <FiGlobe className="register-user-input-icon" />
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    onFocus={() => handleFocus('state')}
                    onBlur={handleBlur}
                    className="register-user-input-field"
                    data-has-value={formData.state ? "true" : "false"}
                    ref={stateRef}
                    required
                    disabled={!formData.country}
                  >
                    <option value="">Select state/province *</option>
                    {formData.country && states.map(state => (
                      <option key={state.isoCode} value={state.name}>{state.name}</option>
                    ))}
                  </select>
                  <div className="register-user-input-border"></div>
                </div>
                {errors.state && (
                  <p className="register-user-field-error fade-in">{errors.state}</p>
                )}
              </div>
            </div>

            <div className="register-user-form-row">
              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper register-user-phone-container ${selectedCountryData ? 'has-country' : ''} ${focusedField === 'mobile' ? 'focused' : ''} ${formData.mobile ? 'filled' : ''} ${errors.mobile ? 'error' : ''}`}>
                  <FiPhone className="register-user-input-icon" />
                  {selectedCountryData && (
                    <span className="register-user-phone-code">
                      +{selectedCountryData.phonecode}
                    </span>
                  )}
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    onFocus={() => handleFocus('mobile')}
                    onBlur={handleBlur}
                    placeholder="Phone *"
                    className="register-user-input-field"
                    ref={mobileRef}
                    required
                  />
                  <div className="register-user-input-border"></div>
                </div>
                {errors.mobile && (
                  <p className="register-user-field-error fade-in">{errors.mobile}</p>
                )}
              </div>

              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper ${focusedField === 'organization' ? 'focused' : ''} ${formData.organization ? 'filled' : ''} ${errors.organization ? 'error' : ''}`}>
                  <FiBriefcase className="register-user-input-icon" />
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    onFocus={() => handleFocus('organization')}
                    onBlur={handleBlur}
                    placeholder="Organization *"
                    className="register-user-input-field"
                    ref={organizationRef}
                    required
                  />
                  <div className="register-user-input-border"></div>
                </div>
                {errors.organization && (
                  <p className="register-user-field-error fade-in">{errors.organization}</p>
                )}
              </div>
            </div>

            <div className="register-user-form-row">
              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper ${focusedField === 'jobTitle' ? 'focused' : ''} ${formData.jobTitle ? 'filled' : ''} ${errors.jobTitle ? 'error' : ''}`}>
                  <FiBriefcase className="register-user-input-icon" />
                  <input
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    onFocus={() => handleFocus('jobTitle')}
                    onBlur={handleBlur}
                    placeholder="Job Title *"
                    className="register-user-input-field"
                    ref={jobTitleRef}
                    required
                  />
                  <div className="register-user-input-border"></div>
                </div>
                {errors.jobTitle && (
                  <p className="register-user-field-error fade-in">{errors.jobTitle}</p>
                )}
              </div>

              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper register-user-password-wrapper ${focusedField === 'password' ? 'focused' : ''} ${formData.password ? 'filled' : ''} ${errors.password ? 'error' : ''}`}>
                  <FiLock className="register-user-input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => handleFocus('password')}
                    onBlur={handleBlur}
                    placeholder="Password *"
                    className="register-user-input-field"
                    ref={passwordRef}
                    required
                  />
                  <button
                    type="button"
                    className="register-user-password-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                  <div className="register-user-input-border"></div>
                </div>
                {errors.password && (
                  <p className="register-user-field-error fade-in">{errors.password}</p>
                )}
              </div>
            </div>

            <div className="register-user-form-row">
              <div className="register-user-input-group">
                <div className={`register-user-input-wrapper register-user-password-wrapper ${focusedField === 'confirmPassword' ? 'focused' : ''} ${formData.confirmPassword ? 'filled' : ''} ${errors.confirmPassword ? 'error' : ''}`}>
                  <FiLock className="register-user-input-icon" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onFocus={() => handleFocus('confirmPassword')}
                    onBlur={handleBlur}
                    placeholder="Confirm Password *"
                    className="register-user-input-field"
                    ref={confirmPasswordRef}
                    required
                  />
                  <button
                    type="button"
                    className="register-user-password-toggle"
                    onClick={toggleConfirmPasswordVisibility}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                  <div className="register-user-input-border"></div>
                </div>
                {errors.confirmPassword && (
                  <p className="register-user-field-error fade-in">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div className="register-user-button-container">              
              <button 
                type="submit" 
                className={`register-user-submit-button ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting}
                aria-label="Register"
              >
                {isSubmitting ? (
                  <div className="register-user-loading-spinner"></div>
                ) : (
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="register-user-submit-icon"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                )}
              </button>
            </div>
          </form>

          <div className="register-user-signin-prompt">
            Already have an account? 
            <Link to="/login" className="register-user-signin-link">Sign In</Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes successPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Register;